import { useRef, useState } from 'react';

interface Props {
  value: string;
  onChange: (url: string) => void;
  label?: string;
  previewHeight?: string;
  hint?: string;
}

export default function ImageUploader({ value, onChange, label, previewHeight = 'h-36', hint }: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [tab, setTab] = useState<'upload' | 'url'>('upload');
  const [urlInput, setUrlInput] = useState(value.startsWith('http') ? value : '');
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');

  const processFile = (file: File) => {
    setError('');
    if (!file.type.startsWith('image/')) {
      setError('Lütfen geçerli bir resim dosyası seçin.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('Dosya boyutu 5MB\'dan küçük olmalıdır.');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      onChange(result);
    };
    reader.readAsDataURL(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  const handleUrlApply = () => {
    if (urlInput.trim()) {
      onChange(urlInput.trim());
    }
  };

  const handleRemove = () => {
    onChange('');
    setUrlInput('');
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      {label && (
        <label className="block text-xs font-medium text-stone-600">{label}</label>
      )}

      {/* Tab Switcher */}
      <div className="flex bg-stone-100 rounded-xl p-1 w-fit">
        <button
          type="button"
          onClick={() => setTab('upload')}
          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            tab === 'upload' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <i className="ri-upload-2-line mr-1" />
          Bilgisayardan Yükle
        </button>
        <button
          type="button"
          onClick={() => setTab('url')}
          className={`px-4 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${
            tab === 'url' ? 'bg-white text-stone-800 shadow-sm' : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <i className="ri-link mr-1" />
          URL ile Ekle
        </button>
      </div>

      {/* Upload Area */}
      {tab === 'upload' && (
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileRef.current?.click()}
          className={`relative border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-all ${
            dragging
              ? 'border-amber-500 bg-amber-50'
              : 'border-stone-200 bg-stone-50 hover:border-amber-400 hover:bg-amber-50/40'
          }`}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
          />
          <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 bg-amber-100 rounded-xl">
            <i className="ri-image-add-line text-amber-600 text-xl" />
          </div>
          <p className="text-sm font-medium text-stone-700">Resim seçmek için tıklayın</p>
          <p className="text-xs text-stone-400 mt-1">veya sürükleyip bırakın · JPG, PNG, WEBP · Maks. 5MB</p>
        </div>
      )}

      {/* URL Area */}
      {tab === 'url' && (
        <div className="flex gap-2">
          <input
            type="url"
            value={urlInput}
            onChange={e => setUrlInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleUrlApply()}
            placeholder="https://..."
            className="flex-1 px-4 py-2.5 text-sm bg-stone-50 rounded-xl border border-stone-200 focus:border-amber-500 focus:outline-none transition-all"
          />
          <button
            type="button"
            onClick={handleUrlApply}
            className="px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-xl transition-all cursor-pointer whitespace-nowrap"
          >
            Uygula
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="text-xs text-red-500 flex items-center gap-1">
          <i className="ri-error-warning-line" /> {error}
        </p>
      )}

      {/* Hint */}
      {hint && !error && (
        <p className="text-xs text-stone-400">{hint}</p>
      )}

      {/* Preview */}
      {value && (
        <div className="relative group rounded-xl overflow-hidden border border-stone-200">
          <div className={`${previewHeight} w-full`}>
            <img
              src={value}
              alt="Önizleme"
              className="w-full h-full object-cover object-top"
              onError={() => setError('Resim yüklenemedi. URL\'yi kontrol edin.')}
            />
          </div>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleRemove(); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-delete-bin-line" /> Kaldır
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
